import { useRequest } from 'features/request/useRequest';
import type { PriceListMtrRowData, ProjectData } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { getPriceListWhereVariables } from '../getWhereVariables';

import { priceListMtr } from './mtrQueries';

interface IUsePriceListMtr {
    contractId: ProjectData['contractId'];
    filterValues: Record<string, string[]>;
    gridRef: RefObject<AgGridReact<PriceListMtrRowData>>;
}

export const usePriceListMtr = ({ contractId, filterValues, gridRef }: IUsePriceListMtr) => {
    const variables = useMemo(
        () => ({
            contractId,
            take: 50,
            order: { code: SortEnumType.Asc },
            where: getPriceListWhereVariables(filterValues),
        }),
        [contractId, filterValues],
    );

    const [priceListMtrData, , , makeRequestPriceListMtr] = useRequest({
        requestId: 'priceListMtr',
        query: priceListMtr,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'priceListMtr',
        requestFn: makeRequestPriceListMtr,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListMtrData?.uerPriceListMtr?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                priceListMtrData.uerPriceListMtr.items,
                priceListMtrData.uerPriceListMtr.totalCount,
            );
        }
    }, [getRowParams, priceListMtrData?.uerPriceListMtr?.items, priceListMtrData?.uerPriceListMtr?.totalCount]);

    const refreshData = useCallback(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.refreshInfiniteCache();
        }
    }, [gridRef]);

    const totalCount = useMemo(
        () => priceListMtrData?.uerPriceListMtr?.totalCount ?? 0,
        [priceListMtrData?.uerPriceListMtr?.totalCount],
    );

    const fetchedCount = useMemo<number>(() => {
        if (!getRowParams?.current?.endRow || !totalCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalCount) {
            return totalCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalCount, getRowParams?.current?.endRow]);

    return useMemo(
        () => ({
            datasource: dataSource,
            totalCount,
            fetchedCount,
            refreshData,
        }),
        [dataSource, totalCount, fetchedCount, refreshData],
    );
};
